"use client";

import * as React from "react";
import {
    LuInfo,
    LuMapPin,
    LuX,
    LuZoomIn,
    LuYoutube,
    LuTwitch,
    LuLink,
} from "react-icons/lu";
import { FaDiscord } from "react-icons/fa6";
import { SiGooglemeet } from "react-icons/si";
import { BiLogoMicrosoftTeams } from "react-icons/bi";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

import { cn } from "~/utils/fontsConfig";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInputV4,
    CommandItem,
    CommandList,
} from "~/components/shadcn/Command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";
import { Input } from "~/components/v4/shadcn/Input";
import { SelectCustomization } from "~/types/CustomizableLayout";

export type Location = {
    type: "physical" | "virtual";
    name?: string;
    address?: string;
    link?: string;
    googleMapsPlaceId?: string;
    googleMapsPlaceName?: string;
    googleMapsPlaceTypes?: string[];
    useGoogleMapsLocation?: boolean;
    city?: string;
    country?: string;
    longitude?: number;
    latitude?: number;
    utcOffset?: number;
};

const virtualEventIcons = {
    zoom: <LuZoomIn className="h-4 w-4" />,
    teams: <BiLogoMicrosoftTeams className="h-4 w-4" />,
    googleMeet: <SiGooglemeet className="h-4 w-4" />,
    youtube: <LuYoutube className="h-4 w-4" />,
    twitch: <LuTwitch className="h-4 w-4" />,
    discord: <FaDiscord className="h-4 w-4" />,
};

const getIcon = (input: string) => {
    if (input.includes("zoom.us")) {
        return virtualEventIcons.zoom;
    }
    if (input.includes("teams.microsoft.com")) {
        return virtualEventIcons.teams;
    }
    if (input.includes("meet.google.com")) {
        return virtualEventIcons.googleMeet;
    }
    if (input.includes("youtube.com")) {
        return virtualEventIcons.youtube;
    }
    if (input.includes("twitch.tv")) {
        return virtualEventIcons.twitch;
    }
    if (input.includes("discord.gg")) {
        return virtualEventIcons.discord;
    }

    return <LuLink className="h-4 w-4" />;
};

interface LocationComboboxProps {
    onLocationSelect?: (location: Location) => void;
    value?: Location;
    placeholder?: string;
    customization?: SelectCustomization;
}

const sanitizeValue = (value: string) => {
    return value.replace(/[^\w\s-]/g, "_");
};

export const LocationSelector = ({
    onLocationSelect,
    value: initialValue,
    placeholder,
    customization,
}: LocationComboboxProps) => {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState<string | undefined>(
        undefined,
    );
    const [places, setPlaces] = React.useState<
        google.maps.places.AutocompletePrediction[]
    >([]);

    const [virtualLocations, setVirtualLocations] = React.useState<
        { location: Location; icon: React.ReactNode }[]
    >([]);

    const places_lib = useMapsLibrary("places");

    React.useEffect(() => {
        if (initialValue?.type === "physical") {
            setInputValue(initialValue.address || initialValue.name);
        } else if (initialValue?.type === "virtual") {
            setInputValue(initialValue.link || initialValue.name);
            if (initialValue.link) {
                setVirtualLocations([
                    {
                        location: initialValue,
                        icon: getIcon(initialValue.link || ""),
                    },
                ]);
            }
        }
    }, [initialValue]);

    const handleSelect = async (location: Location, placeId?: string) => {
        setInputValue(
            location.type === "physical"
                ? location.address || location.name
                : location.link || location.name,
        );
        if (location.type === "physical" && placeId) {
            const details = await getLocationDetails(placeId);

            const cityComponent = details?.address_components?.find(
                (component) =>
                    component.types.includes("locality") ||
                    component.types.includes("administrative_area_level_1"),
            );

            const countryComponent = details?.address_components?.find(
                (component) => component.types.includes("country"),
            );

            location = {
                ...location,
                googleMapsPlaceId: placeId,
                googleMapsPlaceName: details?.name,
                googleMapsPlaceTypes: details?.types,
                useGoogleMapsLocation: true,
                city: cityComponent?.long_name,
                country: countryComponent?.long_name,
                longitude: details?.geometry?.location?.lng(),
                latitude: details?.geometry?.location?.lat(),
                utcOffset: details?.utc_offset_minutes,
            };
        }
        onLocationSelect?.(location);
        setOpen(false);
        setPlaces([]);
    };

    const getLocationDetails = async (
        placeId: string,
    ): Promise<google.maps.places.PlaceResult | null> => {
        if (!places_lib || !placeId) return null;

        const service = new places_lib.PlacesService(
            document.createElement("div"),
        );

        return new Promise((resolve) => {
            service.getDetails(
                {
                    placeId,
                    fields: [
                        "formatted_address",
                        "geometry",
                        "name",
                        "place_id",
                        "types",
                        "website",
                        "formatted_phone_number",
                        "international_phone_number",
                        "address_components",
                        "utc_offset_minutes",
                    ],
                },
                (place: any, status: any) => {
                    if (
                        status === google.maps.places.PlacesServiceStatus.OK &&
                        place
                    ) {
                        resolve(place);
                    } else {
                        resolve(null);
                    }
                },
            );
        });
    };

    const searchPlaces = React.useCallback(
        async (input: string) => {
            if (!places_lib || input.length < 3) {
                setPlaces([]);
                return;
            }

            try {
                const service = new places_lib.AutocompleteService();
                const response = await service.getPlacePredictions({
                    input,
                });

                if (response.predictions) {
                    setPlaces(response.predictions);
                }
            } catch (error) {
                console.error("Error fetching predictions:", error);
                setPlaces([]);
            }
        },
        [places_lib],
    );

    const getPrefixComponent = () => {
        if (initialValue?.type === "physical") {
            return <LuMapPin className="h-4 w-4 opacity-50" />;
        } else if (initialValue?.type === "virtual") {
            return getIcon(initialValue.link || "");
        }
    };

    const handleInputChange = React.useCallback(
        async (input: string) => {
            setInputValue(input);
            setOpen(true);

            // Handle virtual meeting links
            if (
                input.includes("zoom.us") ||
                input.includes("teams.microsoft.com") ||
                input.includes("meet.google.com") ||
                input.includes("youtube.com") ||
                input.includes("twitch.tv") ||
                input.includes("discord.gg") ||
                (input.includes("http") && !input.includes("maps"))
            ) {
                const virtualLocation: Location = {
                    type: "virtual",
                    name: "Virtual Meeting",
                    link: input,
                };
                const icon = getIcon(input);

                setVirtualLocations((prev) => [
                    ...prev,
                    { location: virtualLocation, icon },
                ]);

                return;
            }

            await searchPlaces(input);
        },
        [searchPlaces],
    );

    const resetLocationValue = (locationName?: string) => {
        setInputValue(locationName || "");
        onLocationSelect?.({
            type: "physical",
            name: locationName || "",
            address: "",
            city: "",
            country: "",
            longitude: 0,
            latitude: 0,
            utcOffset: 0,
            useGoogleMapsLocation: false,
            googleMapsPlaceId: undefined,
            googleMapsPlaceName: undefined,
            googleMapsPlaceTypes: undefined,
        });
    };

    return (
        <Popover
            open={open}
            onOpenChange={(isOpen) => {
                setOpen(isOpen);
                if (!isOpen) {
                    setPlaces([]);
                    setVirtualLocations([]);
                }
            }}
        >
            <PopoverTrigger asChild>
                <div className="relative w-full">
                    <Input
                        value={inputValue}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder={
                            placeholder ?? "Add onsite location or virtual link"
                        }
                        className={cn(
                            "h-10 cursor-pointer",
                            open && "border border-v3-clover",
                            customization?.inputClassName,
                        )}
                        onClick={() => setOpen(true)}
                        suffixComponent={
                            <LuX
                                className="h-4 w-4 cursor-pointer opacity-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setInputValue("");
                                    resetLocationValue();
                                    setOpen(false);
                                }}
                            />
                        }
                        prefixComponent={getPrefixComponent()}
                    />
                </div>
            </PopoverTrigger>
            <PopoverContent
                className="mt-[-0.8rem] w-[var(--radix-popover-trigger-width)] border-none p-0"
                align="start"
                side="bottom"
                sideOffset={4}
            >
                <Command shouldFilter={false}>
                    <CommandInputV4
                        placeholder="Search location..."
                        value={inputValue}
                        onValueChange={handleInputChange}
                        wrapperClass={customization?.inputClassName}
                        className={customization?.className}
                    />
                    <CommandList className={customization?.listClassName}>
                        {!!inputValue?.length && places.length === 0 && (
                            <CommandEmpty>No results found.</CommandEmpty>
                        )}

                        {places.length > 0 && (
                            <CommandGroup
                                heading="Places"
                                className={customization?.listClassName}
                            >
                                {places.map((place) => (
                                    <CommandItem
                                        key={place.place_id}
                                        value={sanitizeValue(place.description)}
                                        className={cn(
                                            "cursor-pointer",
                                            customization?.itemClassName,
                                        )}
                                        onSelect={() => {
                                            const location: Location = {
                                                type: "physical",
                                                name: place
                                                    .structured_formatting
                                                    .main_text,
                                                address: place.description,
                                            };
                                            handleSelect(
                                                location,
                                                place.place_id,
                                            );
                                        }}
                                    >
                                        <LuMapPin className="mr-2 h-4 w-4 shrink-0" />
                                        <div className="flex flex-col">
                                            <span>
                                                {
                                                    place.structured_formatting
                                                        .main_text
                                                }
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {
                                                    place.structured_formatting
                                                        .secondary_text
                                                }
                                            </span>
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}

                        {virtualLocations.length > 0 && (
                            <CommandGroup
                                heading="Virtual Options"
                                className={customization?.listClassName}
                            >
                                {virtualLocations.map((location) => (
                                    <CommandItem
                                        value={sanitizeValue(
                                            location.location.link || "",
                                        )}
                                        onSelect={() => {
                                            handleSelect(location.location);
                                            setOpen(false);
                                        }}
                                        className="h-10 cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            {location.icon}
                                            {location.location.link}
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}

                        {inputValue && (
                            <CommandGroup
                                heading="Use exact value"
                                className={customization?.listClassName}
                            >
                                <CommandItem
                                    value={sanitizeValue(inputValue || "")}
                                    onSelect={() => {
                                        resetLocationValue(inputValue);
                                        setOpen(false);
                                    }}
                                    className="h-10 cursor-pointer"
                                >
                                    <LuMapPin className="mr-2 h-4 w-4 shrink-0" />
                                    {inputValue}
                                </CommandItem>
                            </CommandGroup>
                        )}
                    </CommandList>

                    <div
                        className={cn(
                            "flex items-center gap-2 p-4 text-sm text-muted-foreground",
                            customization?.inputClassName,
                        )}
                    >
                        <LuInfo className="h-4 w-4" />
                        <span className="text-xs">
                            If you have a virtual event link, you can enter or
                            paste it above.
                        </span>
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
};
